import { Users, Wifi } from "lucide-react";
import type React from "react";
import { AnimatedCount } from "@/components/AnimatedCount";
import { StatusLight } from "@/components/ui/status-light";
import type { Station } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { getSoundEffects } from "@/services/SoundEffects";

interface StationCardProps {
  station: Station;
  onClick?: () => void;
}

export function StationCard({ station, onClick }: StationCardProps) {
  const soundEffects = getSoundEffects();

  const handleCardClick = (e: React.MouseEvent) => {
    // Only trigger onClick if we didn't click on a button
    if ((e.target as HTMLElement).closest("button")) {
      return;
    }

    // Play click sound
    soundEffects.play("buttonDown");
    setTimeout(() => soundEffects.play("buttonUp"), 150);

    onClick?.();
  };

  return (
    <div
      aria-label={`Station: ${station.name}`}
      className="group relative cursor-pointer transition-all duration-200 hover:shadow-lg border border-gray-200 rounded-lg p-6 bg-white hover:border-gray-300"
      onClick={handleCardClick}
      role="article"
    >
      {/* Station Header */}
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-serif-600 text-lg text-black">
          {station.name}
        </h3>
        {station.isActive && (
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
            <span className="font-serif text-red-500 text-sm">Live</span>
          </div>
        )}
      </div>

      {/* Broadcaster */}
      {station.broadcasterName && (
        <div className="font-serif text-gray-500 text-sm mb-2">
          {station.broadcasterName}
        </div>
      )}

      {/* Now Playing */}
      {station.isActive && station.status === "live" && station.nowPlaying?.track && (
        <div className="font-serif text-gray-600 text-sm mb-3">
          <span className="text-gray-400">♪</span>{" "}
          {station.nowPlaying.artist && (
            <span>
              {station.nowPlaying.artist} -{" "}
            </span>
          )}
          <span>{station.nowPlaying.track}</span>
        </div>
      )}

      {/* Description */}
      {station.description && (
        <p className="font-serif text-gray-600 text-sm mb-4 line-clamp-2">
          {station.description}
        </p>
      )}

      {/* Stats */}
      <div className="flex items-center justify-between border-gray-200 border-t pt-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-400" />
          <span className="font-serif text-gray-600 text-sm">
            <AnimatedCount count={station.viewerCount} />
            {station.viewerCount === 1 ? " listener" : " listeners"}
          </span>
        </div>
        <div className="font-serif text-gray-400 text-sm">
          {station.isActive ? "Click to listen" : "Offline"}
        </div>
      </div>
    </div>
  );
}
